'use client';

import React, { useEffect, useState, useMemo } from 'react';
import AppLayout from '@/components/AppLayout';
import { loadTradesAsync, StoredTrade } from '@/app/utils/tradeStorage';
import { loadBacktestHistoryAsync, BacktestRun } from '@/app/backtest-history/utils/backtestStorage';
import { loadPortfolioAsync, PaperPosition } from '@/app/paper-trading/utils/paperTradingStorage';
import dynamic from 'next/dynamic';
import type { TooltipProps } from 'recharts';
import CorrelationMatrix from './components/CorrelationMatrix';
import PositionHeatMap from './components/PositionHeatMap';
import StrategyComparisonTable from './components/StrategyComparisonTable';

// ─── Recharts dynamic imports ────────────────────────────────────────────────
const ResponsiveContainer = dynamic(() => import('recharts').then((m) => m.ResponsiveContainer), { ssr: false });
const AreaChart = dynamic(() => import('recharts').then((m) => m.AreaChart), { ssr: false });
const Area = dynamic(() => import('recharts').then((m) => m.Area), { ssr: false });
const BarChart = dynamic(() => import('recharts').then((m) => m.BarChart), { ssr: false });
const Bar = dynamic(() => import('recharts').then((m) => m.Bar), { ssr: false });
const LineChart = dynamic(() => import('recharts').then((m) => m.LineChart), { ssr: false });
const Line = dynamic(() => import('recharts').then((m) => m.Line), { ssr: false });
const XAxis = dynamic(() => import('recharts').then((m) => m.XAxis), { ssr: false });
const YAxis = dynamic(() => import('recharts').then((m) => m.YAxis), { ssr: false });
const CartesianGrid = dynamic(() => import('recharts').then((m) => m.CartesianGrid), { ssr: false });
const Tooltip = dynamic(() => import('recharts').then((m) => m.Tooltip), { ssr: false });
const ReferenceLine = dynamic(() => import('recharts').then((m) => m.ReferenceLine), { ssr: false });
const Cell = dynamic(() => import('recharts').then((m) => m.Cell), { ssr: false });
const PieChart = dynamic(() => import('recharts').then((m) => m.PieChart), { ssr: false });
const Pie = dynamic(() => import('recharts').then((m) => m.Pie), { ssr: false });

// ─── Types ───────────────────────────────────────────────────────────────────
interface StrategyStats {
  strategy: string;
  wins: number;
  losses: number;
  winRate: number;
  totalPnl: number;
  trades: number;
}

interface MonthlyPnl {
  month: string;
  pnl: number;
  trades: number;
}

interface EquityPoint {
  date: string;
  cumulative: number;
}

interface DistributionBucket {
  bucket: string;
  count: number;
  type: 'win' | 'loss';
}

// ─── Tooltip components ───────────────────────────────────────────────────────
function PnlTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value as number;
  return (
    <div className="chart-tooltip-card">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className={`text-sm font-semibold font-mono ${val >= 0 ? 'text-positive' : 'text-negative'}`}>
        {val >= 0 ? '+' : ''}${val.toLocaleString('en-US')}
      </p>
      {payload[1] && (
        <p className="text-xs text-muted-foreground mt-0.5">{payload[1].value} trades</p>
      )}
    </div>
  );
}

function EquityTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value as number;
  return (
    <div className="chart-tooltip-card">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className={`text-sm font-semibold font-mono ${val >= 0 ? 'text-positive' : 'text-negative'}`}>
        {val >= 0 ? '+' : ''}${val.toLocaleString('en-US')}
      </p>
    </div>
  );
}

function WinRateTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const winRate = payload[0]?.value as number;
  const pnl = payload[1]?.value as number;
  return (
    <div className="chart-tooltip-card">
      <p className="text-xs font-semibold text-foreground mb-1">{label}</p>
      <p className="text-sm font-semibold text-primary">{winRate?.toFixed(1)}% win rate</p>
      {pnl !== undefined && (
        <p className={`text-xs font-mono mt-0.5 ${pnl >= 0 ? 'text-positive' : 'text-negative'}`}>
          {pnl >= 0 ? '+' : ''}${pnl?.toLocaleString('en-US')} P&L
        </p>
      )}
    </div>
  );
}

function DistTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip-card">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="text-sm font-semibold text-foreground">{payload[0]?.value} trades</p>
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────
interface StatCardProps {
  label: string;
  value: string;
  sub?: string;
  positive?: boolean | null;
}
function StatCard({ label, value, sub, positive }: StatCardProps) {
  const valueColor =
    positive === true ? 'text-positive' : positive === false ? 'text-negative' : 'text-foreground';
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className={`text-xl font-bold font-mono ${valueColor}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function AnalyticsPage() {
  const [trades, setTrades] = useState<StoredTrade[]>([]);
  const [backtests, setBacktests] = useState<BacktestRun[]>([]);
  const [positions, setPositions] = useState<PaperPosition[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      const [t, b, portfolio] = await Promise.all([
        loadTradesAsync(),
        loadBacktestHistoryAsync(),
        loadPortfolioAsync(),
      ]);
      setTrades(t);
      setBacktests(b);
      setPositions(portfolio.positions);
      setLoading(false);
    }
    fetchData();
  }, []);

  // ─── Derived metrics ────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    if (!trades.length) return null;
    const wins = trades.filter((t) => t.pnl > 0);
    const losses = trades.filter((t) => t.pnl <= 0);
    const totalPnl = trades.reduce((s, t) => s + t.pnl, 0);
    const grossProfit = wins.reduce((s, t) => s + t.pnl, 0);
    const grossLoss = Math.abs(losses.reduce((s, t) => s + t.pnl, 0));
    const avgWin = wins.length ? grossProfit / wins.length : 0;
    const avgLoss = losses.length ? grossLoss / losses.length : 0;
    return {
      winRate: (wins.length / trades.length) * 100,
      totalTrades: trades.length,
      totalPnl,
      profitFactor: grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? Infinity : 0,
      avgWin,
      avgLoss,
      expectancy: (wins.length / trades.length) * avgWin - (losses.length / trades.length) * avgLoss,
      bestTrade: Math.max(...trades.map((t) => t.pnl)),
      worstTrade: Math.min(...trades.map((t) => t.pnl)),
    };
  }, [trades]);

  // ─── Risk-adjusted ratios ────────────────────────────────────────────────────
  const riskRatios = useMemo(() => {
    if (trades.length < 2) return null;

    const sorted = [...trades].sort(
      (a, b) => new Date(a.dateISO).getTime() - new Date(b.dateISO).getTime()
    );

    // Daily P&L series
    const dailyMap: Record<string, number> = {};
    sorted.forEach((t) => {
      const key = t.dateISO?.slice(0, 10) ?? t.date;
      if (!key) return;
      dailyMap[key] = (dailyMap[key] ?? 0) + t.pnl;
    });
    const dailyReturns = Object.values(dailyMap);
    if (dailyReturns.length < 2) return null;

    const n = dailyReturns.length;
    const mean = dailyReturns.reduce((s, r) => s + r, 0) / n;

    // Standard deviation of all daily returns
    const variance = dailyReturns.reduce((s, r) => s + (r - mean) ** 2, 0) / (n - 1);
    const stdDev = Math.sqrt(variance);

    // Downside deviation (returns below 0)
    const downsideReturns = dailyReturns.filter((r) => r < 0);
    const downsideVariance =
      downsideReturns.length > 1
        ? downsideReturns.reduce((s, r) => s + r ** 2, 0) / (downsideReturns.length - 1)
        : 0;
    const downsideDev = Math.sqrt(downsideVariance);

    // Annualisation factor (252 trading days)
    const annFactor = Math.sqrt(252);

    // Sharpe ratio (risk-free rate = 0 for simplicity)
    const sharpe = stdDev > 0 ? (mean / stdDev) * annFactor : null;

    // Sortino ratio
    const sortino = downsideDev > 0 ? (mean / downsideDev) * annFactor : null;

    // Calmar ratio: annualised return / max drawdown
    let peak = 0;
    let cumPnl = 0;
    let maxDrawdown = 0;
    sorted.forEach((t) => {
      cumPnl += t.pnl;
      if (cumPnl > peak) peak = cumPnl;
      const dd = peak > 0 ? (peak - cumPnl) / peak : 0;
      if (dd > maxDrawdown) maxDrawdown = dd;
    });
    const totalPnl = sorted.reduce((s, t) => s + t.pnl, 0);
    // Approximate annualised return as total P&L / avg capital proxy (use absolute total as base)
    const calmar =
      maxDrawdown > 0 && Math.abs(totalPnl) > 0
        ? (totalPnl / (peak || Math.abs(totalPnl))) / maxDrawdown
        : null;

    return { sharpe, sortino, calmar, maxDrawdownPct: maxDrawdown * 100 };
  }, [trades]);

  // ─── Strategy win rate breakdown ────────────────────────────────────────────
  const strategyStats = useMemo((): StrategyStats[] => {
    const map: Record<string, { wins: number; losses: number; pnl: number }> = {};
    trades.forEach((t) => {
      const s = t.strategy || 'Unknown';
      if (!map[s]) map[s] = { wins: 0, losses: 0, pnl: 0 };
      if (t.pnl > 0) map[s].wins++;
      else map[s].losses++;
      map[s].pnl += t.pnl;
    });
    return Object.entries(map)
      .map(([strategy, d]) => ({
        strategy,
        wins: d.wins,
        losses: d.losses,
        winRate: ((d.wins / (d.wins + d.losses)) * 100),
        totalPnl: d.pnl,
        trades: d.wins + d.losses,
      }))
      .sort((a, b) => b.trades - a.trades);
  }, [trades]);

  // ─── Monthly P&L ────────────────────────────────────────────────────────────
  const monthlyPnl = useMemo((): MonthlyPnl[] => {
    const map: Record<string, { pnl: number; trades: number }> = {};
    trades.forEach((t) => {
      const d = new Date(t.dateISO);
      if (isNaN(d.getTime())) return;
      const key = `${d.toLocaleString('en-US', { month: 'short' })} ${d.getFullYear().toString().slice(2)}`;
      if (!map[key]) map[key] = { pnl: 0, trades: 0 };
      map[key].pnl += t.pnl;
      map[key].trades++;
    });
    return Object.entries(map)
      .map(([month, d]) => ({ month, pnl: Math.round(d.pnl), trades: d.trades }))
      .sort((a, b) => {
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const [aM, aY] = a.month.split(' ');
        const [bM, bY] = b.month.split(' ');
        return Number(aY) - Number(bY) || months.indexOf(aM) - months.indexOf(bM);
      });
  }, [trades]);

  // ─── Equity curve ────────────────────────────────────────────────────────────
  const equityCurve = useMemo((): EquityPoint[] => {
    const sorted = [...trades].sort(
      (a, b) => new Date(a.dateISO).getTime() - new Date(b.dateISO).getTime()
    );
    let cumulative = 0;
    return sorted.map((t) => {
      cumulative += t.pnl;
      return { date: t.date, cumulative: Math.round(cumulative) };
    });
  }, [trades]);

  // ─── Backtest distribution ────────────────────────────────────────────────────
  const backtestDistribution = useMemo((): DistributionBucket[] => {
    if (!backtests.length) return [];
    const buckets: DistributionBucket[] = [
      { bucket: '<40%', count: 0, type: 'loss' },
      { bucket: '40–50%', count: 0, type: 'loss' },
      { bucket: '50–60%', count: 0, type: 'win' },
      { bucket: '60–70%', count: 0, type: 'win' },
      { bucket: '70–80%', count: 0, type: 'win' },
      { bucket: '>80%', count: 0, type: 'win' },
    ];
    backtests.forEach((b) => {
      const wr = b.results.winRate;
      if (wr < 40) buckets[0].count++;
      else if (wr < 50) buckets[1].count++;
      else if (wr < 60) buckets[2].count++;
      else if (wr < 70) buckets[3].count++;
      else if (wr < 80) buckets[4].count++;
      else buckets[5].count++;
    });
    return buckets;
  }, [backtests]);

  // ─── Backtest KPIs ────────────────────────────────────────────────────────────
  const backtestKpis = useMemo(() => {
    if (!backtests.length) return null;
    const avgWinRate = backtests.reduce((s, b) => s + b.results.winRate, 0) / backtests.length;
    const avgPf = backtests.reduce((s, b) => s + b.results.profitFactor, 0) / backtests.length;
    const bestReturn = Math.max(...backtests.map((b) => b.results.netReturnPct));
    const avgDrawdown = backtests.reduce((s, b) => s + b.results.maxDrawdownPct, 0) / backtests.length;
    return { avgWinRate, avgPf, bestReturn, avgDrawdown, total: backtests.length };
  }, [backtests]);

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <AppLayout>
      <div className="pt-14 lg:pt-0 min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">

          {/* Header */}
          <div className="mb-6">
            <h1 className="text-xl font-bold text-foreground">Performance Analytics</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {loading ? 'Loading your data…' : `${trades.length} trades · ${backtests.length} backtests`}
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-muted-foreground">Loading analytics…</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">

              {/* ── KPI Row ── */}
              {kpis && (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3">
                  <StatCard
                    label="Win Rate"
                    value={`${kpis.winRate.toFixed(1)}%`}
                    sub={`${trades.filter((t) => t.pnl > 0).length}W / ${trades.filter((t) => t.pnl <= 0).length}L`}
                    positive={kpis.winRate >= 50}
                  />
                  <StatCard
                    label="Net P&L"
                    value={`${kpis.totalPnl >= 0 ? '+' : ''}$${Math.round(kpis.totalPnl).toLocaleString()}`}
                    sub={`${kpis.totalTrades} trades`}
                    positive={kpis.totalPnl >= 0}
                  />
                  <StatCard
                    label="Profit Factor"
                    value={isFinite(kpis.profitFactor) ? kpis.profitFactor.toFixed(2) : '∞'}
                    sub="Gross profit / gross loss"
                    positive={kpis.profitFactor >= 1}
                  />
                  <StatCard
                    label="Expectancy"
                    value={`${kpis.expectancy >= 0 ? '+' : ''}$${Math.round(kpis.expectancy).toLocaleString()}`}
                    sub="Per trade average"
                    positive={kpis.expectancy >= 0}
                  />
                </div>
              )}

              {/* ── Risk-Adjusted Ratios ── */}
              {riskRatios && (
                <div className="bg-card border border-border rounded-xl p-5">
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-foreground">Risk-Adjusted Performance</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Key ratios for evaluating strategy quality relative to risk taken
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Sharpe Ratio */}
                    <div className="bg-background border border-border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Sharpe Ratio</p>
                          <p className={`text-2xl font-bold font-mono mt-1 ${
                            riskRatios.sharpe === null ? 'text-muted-foreground' :
                            riskRatios.sharpe >= 1 ? 'text-positive' :
                            riskRatios.sharpe >= 0 ? 'text-foreground' : 'text-negative'
                          }`}>
                            {riskRatios.sharpe === null ? '—' : riskRatios.sharpe.toFixed(2)}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium mt-1 ${
                          riskRatios.sharpe === null ? 'bg-muted text-muted-foreground' :
                          riskRatios.sharpe >= 2 ? 'bg-positive/15 text-positive' :
                          riskRatios.sharpe >= 1 ? 'bg-primary/15 text-primary' :
                          riskRatios.sharpe >= 0 ? 'bg-muted text-muted-foreground': 'bg-negative/15 text-negative'
                        }`}>
                          {riskRatios.sharpe === null ? 'N/A' :
                           riskRatios.sharpe >= 2 ? 'Excellent' :
                           riskRatios.sharpe >= 1 ? 'Good' :
                           riskRatios.sharpe >= 0 ? 'Marginal' : 'Poor'}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Return per unit of total risk (annualised). Above 1.0 is considered good; above 2.0 is excellent.
                      </p>
                    </div>

                    {/* Sortino Ratio */}
                    <div className="bg-background border border-border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Sortino Ratio</p>
                          <p className={`text-2xl font-bold font-mono mt-1 ${
                            riskRatios.sortino === null ? 'text-muted-foreground' :
                            riskRatios.sortino >= 1 ? 'text-positive' :
                            riskRatios.sortino >= 0 ? 'text-foreground' : 'text-negative'
                          }`}>
                            {riskRatios.sortino === null ? '—' : riskRatios.sortino.toFixed(2)}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium mt-1 ${
                          riskRatios.sortino === null ? 'bg-muted text-muted-foreground' :
                          riskRatios.sortino >= 2 ? 'bg-positive/15 text-positive' :
                          riskRatios.sortino >= 1 ? 'bg-primary/15 text-primary' :
                          riskRatios.sortino >= 0 ? 'bg-muted text-muted-foreground': 'bg-negative/15 text-negative'
                        }`}>
                          {riskRatios.sortino === null ? 'N/A' :
                           riskRatios.sortino >= 2 ? 'Excellent' :
                           riskRatios.sortino >= 1 ? 'Good' :
                           riskRatios.sortino >= 0 ? 'Marginal' : 'Poor'}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Like Sharpe but penalises only downside volatility. Higher values indicate better downside-adjusted returns.
                      </p>
                    </div>

                    {/* Calmar Ratio */}
                    <div className="bg-background border border-border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Calmar Ratio</p>
                          <p className={`text-2xl font-bold font-mono mt-1 ${
                            riskRatios.calmar === null ? 'text-muted-foreground' :
                            riskRatios.calmar >= 0.5 ? 'text-positive' :
                            riskRatios.calmar >= 0 ? 'text-foreground' : 'text-negative'
                          }`}>
                            {riskRatios.calmar === null ? '—' : riskRatios.calmar.toFixed(2)}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium mt-1 ${
                          riskRatios.calmar === null ? 'bg-muted text-muted-foreground' :
                          riskRatios.calmar >= 1 ? 'bg-positive/15 text-positive' :
                          riskRatios.calmar >= 0.5 ? 'bg-primary/15 text-primary' :
                          riskRatios.calmar >= 0 ? 'bg-muted text-muted-foreground': 'bg-negative/15 text-negative'
                        }`}>
                          {riskRatios.calmar === null ? 'N/A' :
                           riskRatios.calmar >= 1 ? 'Excellent' :
                           riskRatios.calmar >= 0.5 ? 'Good' :
                           riskRatios.calmar >= 0 ? 'Marginal' : 'Poor'}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Return relative to max drawdown ({riskRatios.maxDrawdownPct.toFixed(1)}% peak-to-trough). Above 0.5 is considered solid.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Row 1: Equity Curve + Monthly P&L ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                {/* Equity Curve */}
                <div className="bg-card border border-border rounded-xl p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">Equity Curve</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">Cumulative P&L across all trades</p>
                    </div>
                    {kpis && (
                      <span className={`text-xs font-mono font-semibold px-2 py-1 rounded-md ${kpis.totalPnl >= 0 ? 'bg-positive/10 text-positive' : 'bg-negative/10 text-negative'}`}>
                        {kpis.totalPnl >= 0 ? '+' : ''}${Math.round(kpis.totalPnl).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="h-[220px]">
                    {equityCurve.length > 1 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={equityCurve} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="analyticsEquityGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.25} />
                              <stop offset="95%" stopColor="var(--primary)" stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                          <XAxis dataKey="date" tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} axisLine={false} tickLine={false} interval={Math.floor(equityCurve.length / 6)} />
                          <YAxis tick={{ fill: 'var(--muted-foreground)', fontSize: 10, fontFamily: 'var(--font-mono)' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} width={44} />
                          <Tooltip content={<EquityTooltip />} />
                          <ReferenceLine y={0} stroke="var(--border)" strokeDasharray="4 4" />
                          <Area type="monotone" dataKey="cumulative" stroke="var(--primary)" strokeWidth={2} fill="url(#analyticsEquityGrad)" dot={false} activeDot={{ r: 4, fill: 'var(--primary)', stroke: 'var(--background)', strokeWidth: 2 }} />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <EmptyChart message="Not enough trade data yet" />
                    )}
                  </div>
                </div>

                {/* Monthly P&L */}
                <div className="bg-card border border-border rounded-xl p-5">
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-foreground">Monthly P&L</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Net profit/loss per calendar month</p>
                  </div>
                  <div className="h-[220px]">
                    {monthlyPnl.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={monthlyPnl} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                          <XAxis dataKey="month" tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fill: 'var(--muted-foreground)', fontSize: 10, fontFamily: 'var(--font-mono)' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} width={44} />
                          <Tooltip content={<PnlTooltip />} />
                          <ReferenceLine y={0} stroke="var(--border)" strokeDasharray="4 4" />
                          <Bar dataKey="pnl" radius={[4, 4, 0, 0]}>
                            {monthlyPnl.map((entry) => (
                              <Cell key={`monthly-${entry.month}`} fill={entry.pnl >= 0 ? 'var(--primary)' : 'var(--negative)'} fillOpacity={0.85} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <EmptyChart message="No monthly data yet" />
                    )}
                  </div>
                </div>
              </div>

              {/* ── Row 2: Strategy Win Rate + Backtest Distribution ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                {/* Strategy Win Rate Breakdown */}
                <div className="bg-card border border-border rounded-xl p-5">
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-foreground">Strategy Win Rate Breakdown</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Win rate and P&L per strategy</p>
                  </div>
                  {strategyStats.length > 0 ? (
                    <>
                      <div className="h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={strategyStats} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                            <XAxis type="number" domain={[0, 100]} tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                            <YAxis type="category" dataKey="strategy" tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }} axisLine={false} tickLine={false} width={68} />
                            <Tooltip content={<WinRateTooltip />} />
                            <Bar dataKey="winRate" radius={[0, 4, 4, 0]}>
                              {strategyStats.map((entry) => (
                                <Cell key={`wr-${entry.strategy}`} fill={entry.winRate >= 50 ? 'var(--primary)' : 'var(--negative)'} fillOpacity={0.85} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      {/* Strategy table */}
                      <div className="mt-4 pt-4 border-t border-border space-y-2">
                        {strategyStats.map((s) => (
                          <div key={`strat-row-${s.strategy}`} className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground w-24 truncate">{s.strategy}</span>
                            <span className="text-foreground font-mono">{s.winRate.toFixed(0)}%</span>
                            <span className="text-muted-foreground">{s.trades} trades</span>
                            <span className={`font-mono font-semibold ${s.totalPnl >= 0 ? 'text-positive' : 'text-negative'}`}>
                              {s.totalPnl >= 0 ? '+' : ''}${Math.round(s.totalPnl).toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <EmptyChart message="No strategy data yet" />
                  )}
                </div>

                {/* Backtest Performance Distribution */}
                <div className="bg-card border border-border rounded-xl p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">Backtest Win Rate Distribution</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">Frequency of backtest win rates</p>
                    </div>
                    {backtestKpis && (
                      <span className="text-xs text-muted-foreground">{backtestKpis.total} runs</span>
                    )}
                  </div>

                  {backtests.length > 0 ? (
                    <>
                      <div className="h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={backtestDistribution} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                            <XAxis dataKey="bucket" tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: 'var(--muted-foreground)', fontSize: 10, fontFamily: 'var(--font-mono)' }} axisLine={false} tickLine={false} width={28} />
                            <Tooltip content={<DistTooltip />} />
                            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                              {backtestDistribution.map((entry) => (
                                <Cell key={`dist-${entry.bucket}`} fill={entry.type === 'win' ? 'var(--primary)' : 'var(--negative)'} fillOpacity={0.8} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      {backtestKpis && (
                        <div className="grid grid-cols-4 gap-2 mt-4 pt-4 border-t border-border">
                          {[
                            { label: 'Avg Win Rate', value: `${backtestKpis.avgWinRate.toFixed(1)}%`, pos: backtestKpis.avgWinRate >= 50 },
                            { label: 'Avg Prof. Factor', value: backtestKpis.avgPf.toFixed(2), pos: backtestKpis.avgPf >= 1 },
                            { label: 'Best Return', value: `+${backtestKpis.bestReturn.toFixed(1)}%`, pos: true },
                            { label: 'Avg Drawdown', value: `-${backtestKpis.avgDrawdown.toFixed(1)}%`, pos: false },
                          ].map((stat) => (
                            <div key={`bt-stat-${stat.label}`} className="text-center">
                              <p className="text-xs text-muted-foreground leading-tight">{stat.label}</p>
                              <p className={`text-sm font-semibold font-mono mt-0.5 ${stat.pos ? 'text-positive' : 'text-negative'}`}>{stat.value}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="h-[200px] flex flex-col items-center justify-center gap-2">
                      <p className="text-sm text-muted-foreground">No backtest runs saved yet</p>
                      <p className="text-xs text-muted-foreground">Run and save backtests to see distribution</p>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Row 3: Correlation Matrix + Position Heat Map ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <CorrelationMatrix trades={trades} />
                <PositionHeatMap positions={positions} trades={trades} />
              </div>

              {/* ── Row 4: Strategy Comparison ── */}
              <StrategyComparisonTable trades={trades} backtests={backtests} />

            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

function EmptyChart({ message }: { message: string }) {
  return (
    <div className="h-full flex items-center justify-center">
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}
